<template>
    <v-dialog v-model="dialogFSC" max-width="1300px" persistent @keydown.esc="handleEscClose">
        <v-card class="d-flex flex-column" style="max-height: 90vh;">
            <!-- Titolo fisso -->
            <v-card-title class="headline d-flex align-center justify-space-between">
                Gestione prodotto in uscita
                <v-btn icon @click="handleEscClose">
                    <v-icon>close</v-icon>
                </v-btn>
            </v-card-title>

            <v-divider />

            <!-- Corpo scorrevole -->
            <v-card-text class="flex-grow-1" style="overflow-y: auto; max-height: calc(90vh - 80px);">
                <v-form ref="form" v-model="formValid">
                    <!-- Sezione FSC -->
                    <div class="mt-4 mb-4">
                        <v-text-field label="Riferimento" v-model="elemento.Riferimento" :rules="[rules.required]"
                            outlined dense />
                        <v-text-field label="Cliente" v-model="elemento.ClienteUscita" :rules="[rules.required]"
                            outlined dense hide-details />
                    </div>

                    <div>
                        <!-- Renderizzo ogni sezione come blocco -->
                        <div v-for="(sezione, index) in elemento.Sezioni" :key="sezione.id"
                            style="border: solid 1px #e0e0e0; padding: 16px; margin-bottom: 20px; border-radius: 4px;">
                            <!-- Header con pulsante Elimina -->
                            <div class="d-flex justify-space-between align-center mb-2">
                                <h2 class="text-h5">Gruppo di prodotto {{ index + 1 }}</h2>
                                <v-btn icon color="error" @click="rimuoviSezione(index)"
                                    v-tooltip.top="'Elimina gruppo di prodotto ' + (index + 1)">
                                    <v-icon>delete</v-icon>
                                </v-btn>
                            </div>

                            <!-- Sezione gruppo di prodotto -->
                            <div>
                                <div class="text-subtitle-2 mb-2">Inserire un gruppo di prodotto da registrare</div>
                                <v-row>
                                    <v-col cols="12" sm="6">
                                        <v-autocomplete label="Gruppo di prodotto" v-model="sezione.GruppoCategoriaFSC"
                                            :items="gruppiProdotto"
                                            :item-text="item => item.codGruppo + ' - ' + item.traduzione"
                                            item-value="codGruppo" return-object :rules="[rules.required]" outlined
                                            dense @change="onGruppoChange(sezione)" hide-details />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <v-autocomplete label="Fattore di conversione"
                                            v-model="sezione.FattoreConversione" :items="getFattoriFiltrati(sezione)"
                                            :item-text="item => item.fattoreConversione + ' - ' + item.gruppoProdotto + ' - ' + item.descrizione"
                                            return-object :rules="[rules.required]" outlined dense hide-details />
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="12" sm="3">
                                        <v-autocomplete :items="um" label="Unità Misura" v-model="sezione.UM"
                                            :rules="[rules.required]" outlined dense hide-details />
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <v-text-field label="Quantità" v-model.number="sezione.Quantita" type="number"
                                            :rules="[rules.required, rules.numeroPositivo]" outlined dense
                                            hide-details />
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <v-select label="Dichiarazione FSC" v-model="sezione.DichiarazioneFSC"
                                            :items="dichiarazioniFSC" item-text="descrizione"
                                            item-value="idDichiarazione" return-object :rules="[rules.required]"
                                            outlined dense hide-details />
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <v-text-field label="Data" v-model="sezione.DataUscita" type="date"
                                            :rules="[rules.required]" outlined dense hide-details />
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete :items="essenze" label="Essenza" v-model="sezione.Essenza"
                                            outlined dense hide-details />
                                    </v-col>
                                </v-row>
                            </div>

                            <!-- Sezione materiale -->
                            <div class="mt-6">
                                <v-divider class="mb-4" />
                                <h3 class="text-h6 mb-2">Materiale {{ index + 1 }}</h3>
                                <div class="text-subtitle-2 mb-2">
                                    Selezionare dalla tabella le materie prime utilizzate e impostare la quantità
                                </div>
                                <v-data-table :headers="headers" :items="sezione.elencoMateriePrime"
                                    item-key="IdMateriale" show-select v-model="sezione.Selected" class="elevation-1"
                                    fixed-header height="250px" hide-default-footer :items-per-page="-1"
                                    :key="aggiornaMatPrime + '-' + index">
                                    <template v-slot:item.UM="{ item }">
                                        <span v-if="mostraMisure(item)">Pezzi</span>
                                        <span v-else>{{ item.UM }}</span>
                                    </template>
                                    <template v-slot:item.QtaRimanente="{ item }">
                                        <span v-if="mostraMisure(item)">{{ item.PezziRimanenti }}</span>
                                        <span v-else>{{ item.QtaRimanente }}</span>
                                    </template>
                                    <template v-slot:item.QtaPrelievo="{ item }">
                                        <v-text-field v-if="!mostraMisure(item)" v-model.number="item.QtaPrelievo"
                                            type="number" dense hide-details outlined :max="item.QtaRimanente" min="0"
                                            style="max-width: 100px" @blur="validateQuantity(item, sezione.Selected)"
                                            @change="validateQuantity(item, sezione.Selected)" />
                                        <v-text-field v-else v-model.number="item.PezziPrelievo" type="number" dense
                                            hide-details outlined :max="item.PezziRimanenti" min="0"
                                            style="max-width: 100px" @blur="validateQuantity(item, sezione.Selected)"
                                            @change="validateQuantity(item, sezione.Selected)" />
                                    </template>
                                </v-data-table>
                            </div>
                        </div>

                        <!-- Pulsante per aggiungere una nuova sezione -->
                        <v-btn @click="aggiungiSezione" class="mb-4 elevation-2" color="primary" outlined>
                            <v-icon left>add</v-icon>
                            Aggiungi gruppo di prodotto
                        </v-btn>
                    </div>

                    <!-- Sezione DDT/Fatture clienti -->
                    <div class="mt-6">
                        <v-divider class="mb-4" />
                        <h2 class="text-h5 mb-4">DDT / Fatture clienti</h2>

                        <div class="text-subtitle-2 mb-4">
                            Inserire i dati di eventuali DDT e fatture dei clienti
                        </div>

                        <!-- Sezione DDT -->
                        <v-card class="mb-6" outlined>
                            <v-card-title class="py-2">
                                <span class="text-h6">DDT</span>
                                <v-spacer />
                                <v-btn icon color="primary" @click="addDDT">
                                    <v-icon>add</v-icon>
                                </v-btn>
                            </v-card-title>

                            <v-data-table :headers="headersDDT" :items="elemento.Ddt" item-key="localId"
                                class="elevation-0" disable-pagination fixed-header height="250px" hide-default-footer
                                :items-per-page="-1" no-data-text="Nessun DDT presente">
                                <template v-slot:item.NumBolla="{ item }">
                                    <v-text-field v-model="item.NumBolla" dense hide-details outlined />
                                </template>
                                <template v-slot:item.DataBolla="{ item }">
                                    <v-text-field v-model="item.DataBolla" type="date" dense hide-details outlined />
                                </template>
                                <template v-slot:item.Descrizione="{ item }">
                                    <v-text-field v-model="item.Descrizione" dense hide-details outlined />
                                </template>
                                <template v-slot:item.actions="{ item }">
                                    <v-btn icon color="error" @click="removeDDT(item)">
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                </template>
                            </v-data-table>
                        </v-card>

                        <!-- Sezione Fatture -->
                        <v-card outlined>
                            <v-card-title class="py-2">
                                <span class="text-h6">Fatture</span>
                                <v-spacer />
                                <v-btn icon color="primary" @click="addFattura">
                                    <v-icon>add</v-icon>
                                </v-btn>
                            </v-card-title>

                            <v-data-table :headers="headersFatture" :items="elemento.Fatture" item-key="localId"
                                class="elevation-0" disable-pagination fixed-header height="250px" hide-default-footer
                                :items-per-page="-1" no-data-text="Nessuna fattura presente">
                                <template v-slot:item.NumDocumento="{ item }">
                                    <v-text-field v-model="item.NumDocumento" dense hide-details outlined />
                                </template>
                                <template v-slot:item.DataDocumento="{ item }">
                                    <v-text-field v-model="item.DataDocumento" type="date" dense hide-details
                                        outlined />
                                </template>
                                <template v-slot:item.Note="{ item }">
                                    <v-text-field v-model="item.Note" dense hide-details outlined />
                                </template>
                                <template v-slot:item.actions="{ item }">
                                    <v-btn icon color="error" @click="removeFattura(item)">
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                </template>
                            </v-data-table>
                        </v-card>
                    </div>

                    <!-- Sezione Inviato a Terzisti -->
                    <div class="mt-6">
                        <v-divider class="mb-4" />
                        <h2 class="text-h5 mb-4">Inviato a terzista</h2>
                        <div class="text-subtitle-2 mb-4">
                            Inserire i dati relativi ai terzisti
                        </div>
                        <v-card outlined>
                            <v-card-title class="py-2">
                                <span class="text-h6">Inviato a terzista</span>
                                <v-spacer />
                                <v-btn icon color="primary" @click="addTerzistaInv">
                                    <v-icon>add</v-icon>
                                </v-btn>
                            </v-card-title>

                            <v-data-table :headers="headersTerzisti" :items="elemento.TerzistiInv" item-key="localId"
                                class="elevation-0" disable-pagination hide-default-footer
                                no-data-text="Nessun terzista presente">
                                <template v-slot:item.Fornitore="{ item }">
                                    <v-text-field v-model="item.Fornitore" dense hide-details outlined />
                                </template>
                                <template v-slot:item.NumBolla="{ item }">
                                    <v-text-field v-model="item.NumBolla" dense hide-details outlined />
                                </template>
                                <template v-slot:item.DataBolla="{ item }">
                                    <v-text-field v-model="item.DataBolla" type="date" dense hide-details outlined />
                                </template>
                                <template v-slot:item.Descrizione="{ item }">
                                    <v-text-field v-model="item.Descrizione" dense hide-details outlined />
                                </template>
                                <template v-slot:item.actions="{ item }">
                                    <v-btn icon color="error" @click="removeTerzistaInv(item)">
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                </template>
                            </v-data-table>
                        </v-card>
                    </div>

                    <!-- Sezione Ricevuto da Terzisti -->
                    <div class="mt-6">
                        <v-divider class="mb-4" />
                        <h2 class="text-h5 mb-4">Ricevuto da terzista</h2>
                        <div class="text-subtitle-2 mb-4">
                            Inserire i dati relativi ai terzisti
                        </div>
                        <v-card outlined>
                            <v-card-title class="py-2">
                                <span class="text-h6">Ricevuto da terzista</span>
                                <v-spacer />
                                <v-btn icon color="primary" @click="addTerzistaRic">
                                    <v-icon>add</v-icon>
                                </v-btn>
                            </v-card-title>

                            <v-data-table :headers="headersTerzisti" :items="elemento.TerzistiRic" item-key="localId"
                                class="elevation-0" disable-pagination hide-default-footer
                                no-data-text="Nessun terzista presente">
                                <template v-slot:item.Fornitore="{ item }">
                                    <v-text-field v-model="item.Fornitore" dense hide-details outlined />
                                </template>
                                <template v-slot:item.NumBolla="{ item }">
                                    <v-text-field v-model="item.NumBolla" dense hide-details outlined />
                                </template>
                                <template v-slot:item.DataBolla="{ item }">
                                    <v-text-field v-model="item.DataBolla" type="date" dense hide-details outlined />
                                </template>
                                <template v-slot:item.Descrizione="{ item }">
                                    <v-text-field v-model="item.Descrizione" dense hide-details outlined />
                                </template>
                                <template v-slot:item.actions="{ item }">
                                    <v-btn icon color="error" @click="removeTerzistaRic(item)">
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                </template>
                            </v-data-table>
                        </v-card>
                    </div>
                </v-form>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn icon color="error" @click="eliminaCommessa" :disabled="!elemento.ID || salvataggioInCorso">
                    <v-icon>delete</v-icon>
                </v-btn>
                <v-spacer />
                <v-btn @click="handleEscClose" text>Annulla</v-btn>
                <v-btn color="primary" @click="salva" :disabled="salvataggioInCorso" :loading="salvataggioInCorso">
                    Salva
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import {
    get_materiali_consumo,
    get_gruppi_prodotto,
    get_fattori_conversione,
    get_dichiarazioni,
    get_um,
    get_essenze,
    set_commessa,
    del_commessa
} from '@/api/api.registroSintesi.js'

export default {
    props: {
        dialogFSC: { type: Boolean, default: false },
        compCode: { type: String, default: '' },
        editItem: { type: Object, default: () => ({}) },
    },

    data() {
        return {
            elemento: {
                Riferimento: '',
                ClienteUscita: '',
                Sezioni: [],
                Ddt: [],
                Fatture: [],
                TerzistiInv: [],
                TerzistiRic: []
            },
            companyCode: "",
            formValid: false,
            salvataggioInCorso: false,

            gruppiProdotto: [],
            fattoriConversione: [],
            dichiarazioniFSC: [],
            um: [],
            essenze: [],

            // Sezione materie prime
            datiMatPrime: [],
            headers: [
                { text: "CategoriaFSC", value: "GruppoCategoriaFSC" },
                { text: "Fornitore", value: "Fornitore" },
                { text: "DDT", value: "NBollaText" },
                { text: "Descrizione", value: "Descrizione" },
                { text: "Quantità disponibile", value: "QtaRimanente" },
                { text: "UM", value: "UM" },
                { text: "Prelievo", value: "QtaPrelievo", sortable: false },
            ],
            aggiornaMatPrime: 0,

            // Sezione DDT/FATTURE
            headersFatture: [
                { text: "Num. Documento", value: "NumDocumento", width: "200px" },
                { text: "Data Documento", value: "DataDocumento", width: "200px" },
                { text: "Note (opzionali)", value: "Note" },
                { text: "", value: "actions", sortable: false, align: "center", width: "80px" },
            ],
            headersDDT: [
                { text: "Num. Bolla", value: "NumBolla", width: "200px" },
                { text: "Data Bolla", value: "DataBolla", width: "200px" },
                { text: "Note (opzionali)", value: "Descrizione" },
                { text: "", value: "actions", sortable: false, align: "center", width: "80px" },
            ],

            // Sezione TERZISTI
            headersTerzisti: [
                { text: "Terzista", value: "Fornitore" },
                { text: "Num. Bolla", value: "NumBolla", width: "150px" },
                { text: "Data Bolla", value: "DataBolla", width: "150px" },
                { text: "Descrizione (opzionale)", value: "Descrizione" },
                { text: "", value: "actions", sortable: false, align: "center", width: "80px" },
            ],

            // Regole di validazione
            rules: {
                required: v => !!v || 'Campo obbligatorio',
                numeroPositivo: v => {
                    if (v === null || v === undefined || v === '') return true;
                    return v > 0 || 'Deve essere un numero positivo';
                }
            }
        };
    },

    methods: {
        async handleEscClose() {
            const confirm = await this.$confirm.open(
                "Chiudi",
                "Chiudere la finestra? Le modifiche non salvate andranno perse"
            );
            if (confirm) {
                this.chiudi(false);
            }
        },

        chiudi(aggiorna) {
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
            this.$emit('close', aggiorna);
        },

        async eliminaCommessa() {
            if (!this.elemento.ID) {
                this.$toast.alert("Nessuna commessa da eliminare");
                return;
            }

            const confirm = await this.$confirm.open(
                "Elimina",
                "Confermare l'eliminazione di tutti i dati di questa vendita?"
            );
            if (!confirm) return;

            this.salvataggioInCorso = true;

            try {
                const res = await del_commessa(this.elemento);
                if (res.data === "OK") {
                    this.chiudi(true);
                } else {
                    this.$toast.error("Errore durante l'eliminazione: " + res.data);
                }
            } catch (error) {
                console.error("Errore durante l'eliminazione:", error);
                this.$toast.error("Errore durante l'eliminazione della commessa");
            } finally {
                this.salvataggioInCorso = false;
            }
        },

        async caricaDati() {
            try {
                await Promise.all([
                    this.caricaDatiCombo(),
                    this.caricaDatiMatPrime()
                ]);
                await this.checkSelezionati();
            } catch (error) {
                console.error("Errore durante il caricamento dei dati:", error);
                this.$toast.error("Errore durante il caricamento dei dati");
            }
        },

        async caricaDatiCombo() {
            try {
                let par = "";
                if (this.role !== "utente") {
                    par = "?companyCode=" + this.compCode + "&tipologia=commessa";
                } else
                    par = "?tipologia=commessa";

                const [gruppiRes, fattoriRes, dichiarazioniRes, umRes, essenzeRes] = await Promise.all([
                    get_gruppi_prodotto(par),
                    get_fattori_conversione(par),
                    get_dichiarazioni(par),
                    get_um(),
                    get_essenze()
                ]);

                this.gruppiProdotto = gruppiRes.data || [];
                this.fattoriConversione = fattoriRes.data || [];
                this.dichiarazioniFSC = dichiarazioniRes.data || [];
                this.um = umRes.data || [];
                this.essenze = essenzeRes.data || [];
            } catch (error) {
                console.error("Errore durante il caricamento delle combo:", error);
                throw error;
            }
        },

        async salva() {
            // Validazione form base
            if (this.$refs.form && !this.$refs.form.validate()) {
                this.$toast.alert("Compilare tutti i campi obbligatori");
                return;
            }

            // Validazione custom completa
            if (!this.validateCommessa(this.elemento)) {
                return;
            }

            this.salvataggioInCorso = true;

            try {
                // Normalizzazione dati prima del salvataggio
                const elementoDaSalvare = this.normalizzaDatiCommessa();

                const res = await set_commessa(elementoDaSalvare);

                if (res.data === "OK") {
                    this.chiudi(true);
                } else {
                    this.$toast.error("Errore durante il salvataggio: " + res.data);
                }
            } catch (error) {
                console.error("Errore durante il salvataggio:", error);
                this.$toast.error("Errore durante il salvataggio della commessa");
            } finally {
                this.salvataggioInCorso = false;
            }
        },

        normalizzaDatiCommessa() {
            const commessa = JSON.parse(JSON.stringify(this.elemento));

            // Normalizza le sezioni
            if (Array.isArray(commessa.Sezioni)) {
                commessa.Sezioni.forEach(sezione => {
                    if (Array.isArray(sezione.Selected)) {
                        sezione.Selected.forEach(item => {
                            if (item.PezziPrelievo === null || item.PezziPrelievo === undefined) {
                                item.PezziPrelievo = 0;
                            }
                            if (item.QtaPrelievo === null || item.QtaPrelievo === undefined) {
                                item.QtaPrelievo = 0;
                            }
                        });
                    }
                });
            }

            return commessa;
        },

        validateCommessa(commessa) {
            // Riferimento
            if (!commessa.Riferimento || commessa.Riferimento.trim() === "") {
                this.$toast.alert("Il campo Riferimento è obbligatorio");
                return false;
            }

            // Cliente
            if (!commessa.ClienteUscita || commessa.ClienteUscita.trim() === "") {
                this.$toast.alert("Il campo Cliente è obbligatorio");
                return false;
            }

            // Sezioni
            if (!Array.isArray(commessa.Sezioni) || commessa.Sezioni.length === 0) {
                this.$toast.alert("È necessario inserire almeno un gruppo di prodotto");
                return false;
            }

            // Validazione di ogni sezione
            for (let i = 0; i < commessa.Sezioni.length; i++) {
                const sezione = commessa.Sezioni[i];
                const numSezione = i + 1;

                if (!sezione.GruppoCategoriaFSC) {
                    this.$toast.alert(`Gruppo prodotto mancante nella sezione ${numSezione}`);
                    return false;
                }

                if (!sezione.FattoreConversione) {
                    this.$toast.alert(`Fattore di conversione mancante nella sezione ${numSezione}`);
                    return false;
                }

                if (!sezione.UM) {
                    this.$toast.alert(`Unità di misura mancante nella sezione ${numSezione}`);
                    return false;
                }

                if (!sezione.Quantita || sezione.Quantita <= 0) {
                    this.$toast.alert(`Quantità non valida nella sezione ${numSezione}`);
                    return false;
                }

                if (!sezione.DichiarazioneFSC) {
                    this.$toast.alert(`Dichiarazione FSC mancante nella sezione ${numSezione}`);
                    return false;
                }

                if (!sezione.DataUscita) {
                    this.$toast.alert(`Data uscita mancante nella sezione ${numSezione}`);
                    return false;
                }

                if (!Array.isArray(sezione.Selected) || sezione.Selected.length === 0) {
                    this.$toast.alert(`Nessun materiale selezionato nella sezione ${numSezione}`);
                    return false;
                }
            }

            // Validazione Fatture - solo date se presenti
            if (Array.isArray(commessa.Fatture)) {
                for (let i = 0; i < commessa.Fatture.length; i++) {
                    if (!commessa.Fatture[i].DataDocumento) {
                        this.$toast.alert(`Data documento mancante nella fattura ${i + 1}`);
                        return false;
                    }
                }
            }

            // Validazione DDT - solo date se presenti
            if (Array.isArray(commessa.Ddt)) {
                for (let i = 0; i < commessa.Ddt.length; i++) {
                    if (!commessa.Ddt[i].DataBolla) {
                        this.$toast.alert(`Data bolla mancante nel DDT ${i + 1}`);
                        return false;
                    }
                }
            }

            // Validazione Terzisti Inviati - solo date se presenti
            if (Array.isArray(commessa.TerzistiInv)) {
                for (let i = 0; i < commessa.TerzistiInv.length; i++) {
                    if (!commessa.TerzistiInv[i].DataBolla) {
                        this.$toast.alert(`Data bolla mancante nel terzista inviato ${i + 1}`);
                        return false;
                    }
                }
            }

            // Validazione Terzisti Ricevuti - solo date se presenti
            if (Array.isArray(commessa.TerzistiRic)) {
                for (let i = 0; i < commessa.TerzistiRic.length; i++) {
                    if (!commessa.TerzistiRic[i].DataBolla) {
                        this.$toast.alert(`Data bolla mancante nel terzista ricevuto ${i + 1}`);
                        return false;
                    }
                }
            }

            return true;
        },

        //#region Gestione Sezioni e Materiali

        getFattoriFiltrati(sezione) {
            const gruppoCategoria = sezione.GruppoCategoriaFSC || sezione.gruppoCategoriaFSC;
            const codGruppo = gruppoCategoria?.codGruppo || gruppoCategoria?.CodGruppo;

            if (!codGruppo) return this.fattoriConversione;

            return this.fattoriConversione.filter(f =>
                f.gruppoProdotto && f.gruppoProdotto.includes(codGruppo)
            );
        },

        onGruppoChange(sezione) {
            // Reset del fattore di conversione quando cambia il gruppo
            sezione.FattoreConversione = null;
        },

        async caricaDatiMatPrime() {
            try {
                let par = "";
                if (this.role !== "utente") {
                    par = "?companyCode=" + this.companyCode;
                }
                if (par === "") {
                    par = "?";
                } else {
                    par += "&";
                }
                par += "commessa=-1&gruppo=";

                const res = await get_materiali_consumo(par);
                this.datiMatPrime = res.data || [];
            } catch (error) {
                console.error("Errore durante il caricamento delle materie prime:", error);
                throw error;
            }
        },

        validateQuantity(item, selected) {
            if (!this.mostraMisure(item)) {
                // Validazione per quantità standard
                if (item.QtaPrelievo > item.QtaRimanente) {
                    item.QtaPrelievo = item.QtaRimanente;
                } else if (item.QtaPrelievo < 0 || item.QtaPrelievo === null) {
                    item.QtaPrelievo = 0;
                }

                // Sincronizza con selected
                if (Array.isArray(selected)) {
                    selected.forEach(s => {
                        if (s.IdMateriale === item.IdMateriale) {
                            s.QtaPrelievo = item.QtaPrelievo;
                        }
                    });
                }
            } else {
                // Validazione per pezzi
                if (item.PezziPrelievo > item.PezziRimanenti) {
                    item.PezziPrelievo = item.PezziRimanenti;
                } else if (item.PezziPrelievo < 0 || item.PezziPrelievo === null) {
                    item.PezziPrelievo = 0;
                }

                // Sincronizza con selected
                if (Array.isArray(selected)) {
                    selected.forEach(s => {
                        if (s.IdMateriale === item.IdMateriale) {
                            s.PezziPrelievo = item.PezziPrelievo;
                        }
                    });
                }
            }
        },

        async aggiungiSezione() {
            const nuovaSezione = {
                id: Math.floor(Date.now() + Math.random()),
                GruppoCategoriaFSC: null,
                FattoreConversione: null,
                UM: null,
                Quantita: null,
                DichiarazioneFSC: null,
                Essenza: null,
                DataUscita: new Date().toISOString().substring(0, 10),
                Selected: [],
                elencoMateriePrime: JSON.parse(JSON.stringify(this.datiMatPrime))
            };

            // Inizializza i campi di prelievo
            nuovaSezione.elencoMateriePrime.forEach(item => {
                item.QtaPrelievo = 0;
                item.PezziPrelievo = 0;
            });

            this.elemento.Sezioni.push(nuovaSezione);
            await this.$nextTick();
            this.aggiornaMatPrime++;
        },

        async checkSelezionati() {
            if (!this.elemento.Sezioni || this.elemento.Sezioni.length === 0) {
                return;
            }

            this.elemento.Sezioni.forEach(sezione => {
                let elencoClonato = JSON.parse(JSON.stringify(this.datiMatPrime));

                if (sezione.Selected && sezione.Selected.length > 0) {
                    elencoClonato.forEach(item => {
                        let selezionato = sezione.Selected.find(sel => sel.IdMateriale === item.IdMateriale);
                        if (selezionato) {
                            item.QtaPrelievo = selezionato.QtaPrelievo || 0;
                            item.QtaRimanente += item.QtaPrelievo;
                            item.PezziPrelievo = selezionato.PezziPrelievo || 0;
                            item.PezziRimanenti += item.PezziPrelievo;
                        } else {
                            item.QtaPrelievo = 0;
                            item.PezziPrelievo = 0;
                        }
                    });
                } else {
                    elencoClonato.forEach(item => {
                        item.QtaPrelievo = 0;
                        item.PezziPrelievo = 0;
                    });
                }

                sezione.elencoMateriePrime = elencoClonato.filter(item => item.QtaRimanente > 0);
            });

            this.aggiornaMatPrime++;
        },

        async rimuoviSezione(index) {
            const confirm = await this.$confirm.open(
                "Elimina",
                `Eliminare il gruppo di prodotto ${index + 1}?`
            );
            if (!confirm) return;

            this.elemento.Sezioni.splice(index, 1);
        },

        mostraMisure(item) {
            if (!item) return false;

            const codGruppo = item.CodGruppoCategoriaFSC || item.GruppoCategoriaFSC;
            return codGruppo === 'W9.1' || codGruppo === 'W9.6' || codGruppo === 'W5.2' || codGruppo === 'W6.1';
        },

        //#endregion

        //#region Gestione DDT e Fatture

        addFattura() {
            this.elemento.Fatture.push({
                localId: Date.now() + Math.random(),
                IdCommessa: this.elemento.ID,
                Prog: -1,
                NumDocumento: "",
                DataDocumento: new Date().toISOString().substring(0, 10),
                Note: "",
            });
        },

        removeFattura(item) {
            this.elemento.Fatture = this.elemento.Fatture.filter(f => f.localId !== item.localId);
        },

        addDDT() {
            this.elemento.Ddt.push({
                localId: Date.now() + Math.random(),
                IdCommessa: this.elemento.ID,
                Prog: -1,
                NumBolla: "",
                DataBolla: new Date().toISOString().substring(0, 10),
                Descrizione: "",
            });
        },

        removeDDT(item) {
            this.elemento.Ddt = this.elemento.Ddt.filter(d => d.localId !== item.localId);
        },

        //#endregion

        //#region Gestione Terzisti

        addTerzistaInv() {
            this.elemento.TerzistiInv.push({
                localId: Date.now() + Math.random(),
                IdCommessa: this.elemento.ID,
                Prog: -1,
                Fornitore: "",
                NumBolla: "",
                DataBolla: new Date().toISOString().substring(0, 10),
                TipoDocumento: "",
                Descrizione: "",
            });
        },

        removeTerzistaInv(item) {
            this.elemento.TerzistiInv = this.elemento.TerzistiInv.filter(t => t.localId !== item.localId);
        },

        addTerzistaRic() {
            this.elemento.TerzistiRic.push({
                localId: Date.now() + Math.random(),
                IdCommessa: this.elemento.ID,
                Prog: -1,
                Fornitore: "",
                NumBolla: "",
                DataBolla: new Date().toISOString().substring(0, 10),
                TipoDocumento: "",
                Descrizione: "",
            });
        },

        removeTerzistaRic(item) {
            this.elemento.TerzistiRic = this.elemento.TerzistiRic.filter(t => t.localId !== item.localId);
        },

        //#endregion

        initElemento() {
            // Inizializza l'elemento con valori di default se necessario
            if (!this.elemento.Sezioni) this.elemento.Sezioni = [];
            if (!this.elemento.Ddt) this.elemento.Ddt = [];
            if (!this.elemento.Fatture) this.elemento.Fatture = [];
            if (!this.elemento.TerzistiInv) this.elemento.TerzistiInv = [];
            if (!this.elemento.TerzistiRic) this.elemento.TerzistiRic = [];
        }
    },

    mounted() {
        this.companyCode = this.$route.params.codice || "";
        if (!this.companyCode) {
            this.$router.push({ path: "/" });
        }
    },

    watch: {
        editItem: {
            handler(newVal) {
                if (newVal && Object.keys(newVal).length > 0) {
                    this.elemento = JSON.parse(JSON.stringify(newVal));
                    this.initElemento();
                }
            },
            deep: true,
            immediate: true
        },

        dialogFSC(newVal) {
            if (newVal) {
                this.caricaDati();
                this.$nextTick(() => {
                    if (this.$refs.form) {
                        this.$refs.form.resetValidation();
                    }
                });
            }
        }
    },

    computed: {
        role() {
            return this.$store.getters['user/getRole'];
        }
    }
};
</script>

<style scoped>
.v-data-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
}
</style>